import {
    asArray,
    formatDelta,
    formatKpiPct,
    formatScorePct,
    readField,
    scoreTone,
} from './utils'

const num = value => {
    const n = Number(value)
    return Number.isFinite(n) ? n : 0
}

const textOf = (obj, ...keys) => {
    const value = readField(obj, ...keys)
    if (value === undefined || value === null) return ''
    return String(value)
}

const Score = ({ value }) => {
    const tone = scoreTone(value)
    return <span className={`score ${tone}`}>{formatScorePct(value)}</span>
}

const Meter = ({ value }) => {
    const tone = scoreTone(value)
    const width =
        tone === 'empty' ? 0 : Math.min(100, Math.max(0, Number(value)))
    return (
        <span className={`meter ${tone}`}>
            <i style={{ width: `${width}%` }} />
        </span>
    )
}

const SectionTable = ({ sections }) => {
    if (!sections.length) {
        return <div className="empty">No section data for this period.</div>
    }
    return (
        <div className="tbl">
            <table>
                <thead>
                    <tr>
                        <th>SECTION</th>
                        <th>ASKED</th>
                        <th>NC</th>
                        <th>COMPLIANCE</th>
                        <th>SCORE</th>
                    </tr>
                </thead>
                <tbody>
                    {sections.map(section => {
                        const stats = readField(section, 'Stats', 'stats') || {}
                        const name = textOf(
                            section,
                            'SectionName',
                            'sectionName',
                            'Name',
                            'name',
                        )
                        const compliance = readField(
                            stats,
                            'Compliance',
                            'compliance',
                        )
                        const questions = asArray(
                            readField(section, 'Questions', 'questions'),
                        )
                        return (
                            <tr key={name || JSON.stringify(stats)}>
                                <td>
                                    <b>{name || 'Section'}</b>
                                    {questions.length > 0 && (
                                        <details className="questions">
                                            <summary>
                                                Question detail ({questions.length})
                                            </summary>
                                            <QuestionTable questions={questions} />
                                        </details>
                                    )}
                                </td>
                                <td>{num(readField(stats, 'Asked', 'asked'))}</td>
                                <td>{num(readField(stats, 'Nc', 'nc'))}</td>
                                <td>
                                    <Meter value={compliance} />
                                </td>
                                <td>
                                    <Score value={compliance} />
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

const QuestionTable = ({ questions }) => (
    <div className="tbl">
        <table>
            <thead>
                <tr>
                    <th>QUESTION</th>
                    <th>ASKED</th>
                    <th>NC</th>
                    <th>SCORE</th>
                </tr>
            </thead>
            <tbody>
                {questions.map(question => {
                    const stats = readField(question, 'Stats', 'stats') || {}
                    const label = textOf(
                        question,
                        'Question',
                        'question',
                        'QuestionText',
                        'Name',
                        'name',
                    )
                    return (
                        <tr key={label}>
                            <td>{label || 'Question'}</td>
                            <td>{num(readField(stats, 'Asked', 'asked'))}</td>
                            <td>{num(readField(stats, 'Nc', 'nc'))}</td>
                            <td>
                                <Score
                                    value={readField(
                                        stats,
                                        'Compliance',
                                        'compliance',
                                    )}
                                />
                            </td>
                        </tr>
                    )
                })}
            </tbody>
        </table>
    </div>
)

const driverLabel = driver =>
    textOf(
        driver,
        'Question',
        'question',
        'Name',
        'name',
        'Driver',
        'driver',
        'Label',
        'label',
    )

const TopDrivers = ({ drivers }) => {
    const rows = drivers.filter(driver => driverLabel(driver)).slice(0, 5)
    if (!rows.length) return null
    return (
        <div>
            <div className="band">TOP NON-COMPLIANT DRIVERS</div>
            <div className="tbl" style={{ marginTop: 8 }}>
                <table>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>DRIVER</th>
                            <th>SECTION</th>
                            <th>NC</th>
                            <th>SCORE</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((driver, index) => {
                            const stats =
                                readField(driver, 'Stats', 'stats') || driver
                            return (
                                <tr key={`${driverLabel(driver)}-${index}`}>
                                    <td>{index + 1}</td>
                                    <td>{driverLabel(driver)}</td>
                                    <td>
                                        {textOf(
                                            driver,
                                            'SectionName',
                                            'sectionName',
                                        ) || '–'}
                                    </td>
                                    <td>
                                        {num(
                                            readField(
                                                stats,
                                                'Nc',
                                                'nc',
                                                'Count',
                                                'count',
                                            ),
                                        )}
                                    </td>
                                    <td>
                                        <Score
                                            value={readField(
                                                stats,
                                                'Compliance',
                                                'compliance',
                                            )}
                                        />
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

const Trend = ({ points }) => (
    <div>
        <div className="band">12-MONTH TREND</div>
        <div className="trend-scroll">
            <div
                className="trend"
                style={{
                    gridTemplateColumns: `repeat(${Math.max(points.length, 1)}, minmax(36px, 1fr))`,
                    minWidth: Math.max(points.length, 1) * 40,
                }}
            >
                {points.map((point, index) => {
                    const overall = readField(point, 'Overall', 'overall') || {}
                    const compliance = readField(
                        overall,
                        'Compliance',
                        'compliance',
                    )
                    const tone = scoreTone(compliance)
                    const height =
                        tone === 'empty'
                            ? 0
                            : Math.min(100, Math.max(0, Number(compliance)))
                    const selected = Boolean(
                        readField(point, 'IsSelected', 'isSelected'),
                    )
                    const short = textOf(point, 'Short', 'short', 'Label', 'label')
                    return (
                        <div
                            className={`col${selected ? ' sel' : ''}`}
                            key={`${short}-${index}`}
                        >
                            <i
                                className={`bar ${tone}`}
                                style={{ height: `${Math.round(height * 0.72)}px` }}
                            />
                            <span className="pct">
                                {tone === 'empty' ? '–' : formatScorePct(compliance)}
                            </span>
                            <span className="name">{short}</span>
                        </div>
                    )
                })}
            </div>
        </div>
    </div>
)

const CustomerBlocks = ({ customers }) => {
    if (!customers.length) {
        return <div className="empty">No customer rows for this period.</div>
    }
    const ranked = [...customers].sort(
        (a, b) =>
            num(readField(a, 'Rank', 'rank')) - num(readField(b, 'Rank', 'rank')),
    )
    return (
        <div className="stack">
            <div>
                <div className="band">CUSTOMER RANKING</div>
                <div className="tbl" style={{ marginTop: 8 }}>
                    <table>
                        <thead>
                            <tr>
                                <th>RANK</th>
                                <th>CUSTOMER</th>
                                <th>AUDITS</th>
                                <th>NC</th>
                                <th>SCORE</th>
                                <th>STATUS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ranked.map(customer => {
                                const overall =
                                    readField(customer, 'Overall', 'overall') || {}
                                const compliance = readField(
                                    overall,
                                    'Compliance',
                                    'compliance',
                                )
                                const tone = scoreTone(compliance)
                                const name = textOf(customer, 'Name', 'name')
                                return (
                                    <tr key={`${readField(customer, 'Rank', 'rank')}-${name}`}>
                                        <td>{readField(customer, 'Rank', 'rank') ?? '–'}</td>
                                        <td>
                                            <b>{name || 'Customer'}</b>
                                        </td>
                                        <td>{num(readField(overall, 'Audits', 'audits'))}</td>
                                        <td>{num(readField(overall, 'Nc', 'nc'))}</td>
                                        <td>
                                            <Score value={compliance} />
                                        </td>
                                        <td>
                                            <span className={`status ${tone}`}>
                                                {textOf(
                                                    customer,
                                                    'StatusLabel',
                                                    'statusLabel',
                                                ) || '–'}
                                            </span>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            {ranked.map(customer => {
                const overall = readField(customer, 'Overall', 'overall') || {}
                const name = textOf(customer, 'Name', 'name') || 'Customer'
                const rank = readField(customer, 'Rank', 'rank')
                const compliance = readField(overall, 'Compliance', 'compliance')
                const tone = scoreTone(compliance)
                const sections = asArray(
                    readField(customer, 'Sections', 'sections'),
                )
                return (
                    <article className="customer-card" key={`${rank}-${name}`}>
                        <div className="customer-head">
                            <div>
                                <h3>
                                    {rank ? `${rank}. ` : ''}
                                    {name}
                                </h3>
                                <div className="customer-meta">
                                    {num(readField(overall, 'Audits', 'audits'))} audits
                                    {' · '}
                                    {num(readField(overall, 'Nc', 'nc'))} non-compliant
                                    {' · '}
                                    {formatKpiPct(compliance)} overall
                                </div>
                            </div>
                            <span className={`status ${tone}`}>
                                {textOf(customer, 'StatusLabel', 'statusLabel') ||
                                    formatScorePct(compliance)}
                            </span>
                        </div>
                        <SectionTable sections={sections} />
                    </article>
                )
            })}
        </div>
    )
}

export const ProcessHealthPreview = ({ data, reportType, scopeLabel }) => {
    const overall = readField(data, 'Overall', 'overall') || {}
    const compliance = readField(overall, 'Compliance', 'compliance')
    const delta = formatDelta(readField(data, 'DeltaVsPrevious', 'deltaVsPrevious'))
    const previous = textOf(data, 'PreviousShort', 'previousShort')
    const period =
        textOf(data, 'PeriodLabel', 'periodLabel') || 'Selected month'
    const trend = asArray(readField(data, 'Trend', 'trend'))
    const sections = asArray(readField(data, 'Sections', 'sections'))
    const customers = asArray(readField(data, 'Customers', 'customers'))
    const rootDrivers = asArray(
        readField(data, 'TopDrivers', 'topDrivers', 'TopNcDrivers'),
    )
    const drivers = rootDrivers.length
        ? rootDrivers
        : sections.flatMap(section =>
              asArray(readField(section, 'TopDrivers', 'topDrivers')),
          )
    const showTrend = reportType !== 'ByCustomer' && trend.length > 0
    const showCustomers = reportType === 'ByCustomer'
    const showSections = sections.length > 0 || !showCustomers

    return (
        <section className="preview" aria-label="On-screen preview">
            <div className="preview-bar">
                Preview · {period} · {scopeLabel}
            </div>
            <div className="kpis">
                <div className="kpi">
                    <b>{num(readField(overall, 'Audits', 'audits'))}</b>
                    <span className="cap">Audits completed</span>
                </div>
                <div className="kpi red">
                    <b>{num(readField(overall, 'Nc', 'nc'))}</b>
                    <span className="cap">Non-compliant</span>
                </div>
                <div className="kpi">
                    <b>{num(readField(overall, 'Asked', 'asked'))}</b>
                    <span className="cap">Questions answered</span>
                </div>
                <div className="kpi">
                    <b>{formatKpiPct(compliance)}</b>
                    <span className="cap">
                        Overall
                        {delta && (
                            <>
                                {' · '}
                                <span className={`delta ${delta.direction}`}>
                                    {delta.direction === 'down' ? '▼' : '▲'}{' '}
                                    {delta.points} pts vs{' '}
                                    {(previous || 'previous').toUpperCase()}
                                </span>
                            </>
                        )}
                    </span>
                </div>
            </div>
            {showTrend && <Trend points={trend} />}
            {showSections && (
                <div>
                    <div className="band">SECTION SUMMARY</div>
                    <div style={{ marginTop: 8 }}>
                        <SectionTable sections={sections} />
                    </div>
                </div>
            )}
            <TopDrivers drivers={drivers} />
            {showCustomers && <CustomerBlocks customers={customers} />}
        </section>
    )
}
